import { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { MessageCircle, Paperclip } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  fetchUserTeams,
  fetchMessages,
  sendMessageApi,
  editMessageApi,
  markAsRead,
  downloadFile,
} from "../../api/chat";

// ── Message list (same style as user chat) ────────────────────────────────────
function MessageList({
  messages,
  currentUserId,
  openMenuIndex,
  setOpenMenuIndex,
  onEditSave,
  token,
  socket,
}: {
  messages: any[];
  currentUserId: string;
  openMenuIndex: number | null;
  setOpenMenuIndex: (i: number | null) => void;
  onEditSave: (msgId: string, newText: string, original: string, index: number, setErr: (i: number | null) => void) => void;
  token: string;
  socket: any;
}) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editTipIndex, setEditTipIndex] = useState<number | null>(null);
  const [editErrorIndex, setEditErrorIndex] = useState<number | null>(null);
  const [editWindowExpiredIndices, setEditWindowExpiredIndices] = useState<Set<number>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  useEffect(() => {
    if (!socket || !currentUserId) return;
    messages.forEach((msg) => {
      const senderId = String(msg.senderId?._id ?? msg.senderId);
      if (senderId !== currentUserId && msg.status !== "seen") {
        socket.emit("message:seen", { messageId: msg._id, senderId });
      }
    });
  }, [messages, socket, currentUserId]);

  useEffect(() => {
    if (openMenuIndex === null) return;
    const handler = () => setOpenMenuIndex(null);
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openMenuIndex]);

  // Start inline edit — check 30s window first
  const startEdit = (i: number, text: string) => {
    const msg = messages[i];
    const age = Date.now() - new Date(msg.createdAt).getTime();
    if (age > 30000) {
      setEditErrorIndex(i);
      setTimeout(() => setEditErrorIndex(null), 4000);
      setOpenMenuIndex(null);
      return;
    }
    setEditingText(text);
    setEditingIndex(i);
    setOpenMenuIndex(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setEditingIndex(null);
      setEditTipIndex(i);
      setEditWindowExpiredIndices(prev => {
        const newSet = new Set(prev);
        newSet.add(i);
        return newSet;
      });
      setTimeout(() => setEditTipIndex(null), 3000);
    }, 30000 - age);
  };

  const handleSave = (i: number) => {
    if (!editingText.trim()) return;
    onEditSave(messages[i]._id, editingText.trim(), messages[i].encryptedContent, i, setEditErrorIndex);
    setEditingIndex(null);
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  return (
    <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto w-full overflow-x-hidden">
      <div className="flex flex-col gap-3 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4">
        {messages.map((msg, i) => {
          const isMine = String(msg.senderId?._id ?? msg.senderId) === currentUserId;
          const myMessages = messages.filter(m => String(m.senderId?._id ?? m.senderId) === currentUserId);
          const lastSeenMsg = [...myMessages].reverse().find(m => m.status === "seen");
          const isLastSeenMsg = isMine && msg.status === "seen" && lastSeenMsg?._id === msg._id;

          const messageAge = Date.now() - new Date(msg.createdAt).getTime();
          const isEditExpired = editWindowExpiredIndices.has(i) || messageAge > 30000;

          return (
            <div key={msg._id ?? i} className={`flex w-full ${isMine ? "justify-end" : "justify-start"}`}>
              {isMine ? (
                <div className="relative max-w-xs sm:max-w-sm">
                  <div className="bg-[#0070ad] min-h-[41px] rounded-[16px] px-3 sm:px-4 py-2 flex items-center gap-2 break-words">
                    {editingIndex === i && !isEditExpired ? (
                      <input
                        autoFocus
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSave(i);
                          if (e.key === "Escape") { setEditingIndex(null); if (timerRef.current) clearTimeout(timerRef.current); }
                        }}
                        className="bg-transparent text-white font-['Inter:Regular',sans-serif] text-[14px] sm:text-[16px] leading-[24px] focus:outline-none border-b border-white flex-1"
                      />
                    ) : (
                      <div className="flex flex-col flex-1">
                        {msg.messageType === "file" ? (
                          <button
                            onClick={() => downloadFile(token, msg.fileUrl, msg.fileName)}
                            className="text-white text-[14px] sm:text-[16px] leading-[24px] underline underline-offset-2 flex items-center gap-1 text-left"
                          >
                            📎 {msg.fileName}
                          </button>
                        ) : (
                          <p className="font-['Inter:Regular',sans-serif] font-normal text-[14px] sm:text-[16px] text-white leading-[24px] break-words">
                            {msg.encryptedContent}
                          </p>
                        )}
                        {msg.isEdited && <p className="italic text-[10px] sm:text-[11px] text-[rgba(255,255,255,0.7)] leading-[16px]">Edited</p>}
                      </div>
                    )}
                    <button
                      onMouseDown={(e) => { e.stopPropagation(); setOpenMenuIndex(openMenuIndex === i ? null : i); }}
                      className="relative rounded-[4px] shrink-0 size-[24px] flex items-center justify-center"
                    >
                      <svg className="size-[16px]" fill="none" viewBox="0 0 16 16">
                        <path d="M8 8.66667C8.36819 8.66667 8.66667 8.36819 8.66667 8C8.66667 7.63181 8.36819 7.33333 8 7.33333C7.63181 7.33333 7.33333 7.63181 7.33333 8C7.33333 8.36819 7.63181 8.66667 8 8.66667Z" stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
                        <path d="M8 4C8.36819 4 8.66667 3.70152 8.66667 3.33333C8.66667 2.96514 8.36819 2.66667 8 2.66667C7.63181 2.66667 7.33333 2.96514 7.33333 3.33333C7.33333 3.70152 7.63181 4 8 4Z" stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
                        <path d="M8 13.3333C8.36819 13.3333 8.66667 13.0349 8.66667 12.6667C8.66667 12.2985 8.36819 12 8 12C7.63181 12 7.33333 12.2985 7.33333 12.6667C7.33333 13.0349 7.63181 13.3333 8 13.3333Z" stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
                      </svg>
                    </button>
                  </div>

                  {editTipIndex === i && (
                    <div className="absolute right-0 top-full mt-1 bg-[#121a38] text-white text-[11px] sm:text-[12px] px-[10px] py-[6px] rounded-[8px] whitespace-nowrap z-10">Edit time ended</div>
                  )}

                  {isLastSeenMsg && (
                    <div className="flex justify-end mt-0.5 pr-1">
                      <svg className="size-[13px] text-[#00d5d0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </div>
                  )}

                  {editErrorIndex === i && (
                    <p className="text-[10px] sm:text-[11px] text-red-500 mt-0.5 text-right pr-1">Edit time ended. Send a new message.</p>
                  )}

                  {openMenuIndex === i && (
                    <div onMouseDown={(e) => e.stopPropagation()} className="absolute bg-white flex flex-col items-start right-0 top-[41px] pb-[0.667px] pt-[4.667px] px-[0.667px] rounded-[10px] w-[160px] z-10">
                      <div aria-hidden="true" className="absolute border-[#e5e7eb] border-[0.667px] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_4px_6px_0px_rgba(0,0,0,0.1),0px_2px_4px_0px_rgba(0,0,0,0.06)]" />
                      <button onClick={() => { navigator.clipboard.writeText(msg.encryptedContent); setOpenMenuIndex(null); }} className="h-[36px] relative shrink-0 w-full hover:bg-gray-50 transition-colors text-left">
                        <p className="absolute font-['Inter:Medium',sans-serif] font-medium leading-[20px] left-[40px] not-italic text-[#364153] text-[14px] top-[7.67px] whitespace-nowrap">Copy</p>
                      </button>
                      {msg.messageType !== "file" && !isEditExpired && (
                        <button onClick={() => startEdit(i, msg.encryptedContent)} className="h-[36px] relative shrink-0 w-full hover:bg-gray-50 transition-colors text-left">
                          <p className="absolute font-['Inter:Medium',sans-serif] font-medium leading-[20px] left-[40px] not-italic text-[#364153] text-[14px] top-[7.67px] whitespace-nowrap">Edit</p>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-[#f3f4f6] rounded-[16px] px-3 sm:px-4 py-2 max-w-xs sm:max-w-sm break-words">
                  {msg.messageType === "file" ? (
                    <button
                      onClick={() => downloadFile(token, msg.fileUrl, msg.fileName)}
                      className="text-[#0058ab] text-[14px] sm:text-[16px] leading-[24px] underline underline-offset-2 flex items-center gap-1 text-left"
                    >
                      📎 {msg.fileName}
                    </button>
                  ) : (
                    <p className="font-['Inter:Regular',sans-serif] font-normal text-[14px] sm:text-[16px] text-[#101828] leading-[24px] break-words">
                      {msg.encryptedContent}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

// Icon for attach
function Icon7() {
  return (
    <Paperclip
      size={20}
      className="text-[#6A7282]"
      strokeWidth={2}
      aria-label="Attach file"
    />
  );
}
 

// File attach button
function Button5({ onFileSelect }: { onFileSelect: (f: File) => void }) {
  return (
    <label className="relative rounded-[10px] shrink-0 size-[36px] cursor-pointer hover:bg-gray-100 transition-colors" data-name="Button">
      <input type="file" className="hidden" onChange={(e) => { if (e.target.files?.[0]) onFileSelect(e.target.files[0]); }} />
      <div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex flex-col items-start pt-[8px] px-[8px] relative size-full">
        <Icon7 />
      </div>
    </label>
  );
}

// Text input
function TextArea({ value, onChange, onSend }: { value: string; onChange: (v: string) => void; onSend: () => void }) {
  return (
    <div className="flex-[1_0_0] h-[41.333px] min-h-px min-w-px relative rounded-[10px]" data-name="Text Area">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
        placeholder="Type a message..."
        className="bg-white border border-[#d1d5db] w-full h-full rounded-[10px] px-[16px] py-[8px] text-[16px] leading-[24px] text-[rgba(18,26,56,0.8)] focus:outline-none focus:border-[#0058ab]"
      />
    </div>
  );
}

interface MentorInfo {
  _id: string; // Composite ID for UI uniqueness
  mentorId: string; // Actual mentor user ID for API calls
  name: string;
  email: string;
  username?: string;
  isOnline?: boolean;
  hackathonId?: string;
  hackathonName?: string;
}

export default function ExpertConnect() {
  const { user, token } = useAuth();
  const [mentors, setMentors] = useState<MentorInfo[]>([]);
  const [selectedMentor, setSelectedMentor] = useState<MentorInfo | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [message, setMessage] = useState("");
  const [loadingMentors, setLoadingMentors] = useState(true);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const socketRef = useRef<any>(null);

  // 1. Fetch assigned mentors from all teams
  useEffect(() => {
    if (!token) return;
    setLoadingMentors(true);
    
    // Fetch all teams to get all mentor-hackathon assignments
    fetchUserTeams(token)
      .then((teams: any) => {
        const teamList = Array.isArray(teams) ? teams : [];
        
        // Build explicit mentor-hackathon assignments from teams
        // Each team represents a unique (mentor, hackathon) pair
        const mentorAssignments: Array<{
          mentorId: string;
          mentorName: string;
          hackathonId: string;
          hackathonName: string;
          isOnline: boolean;
          username: string;
        }> = [];
        
        const seenPairs = new Set<string>();
        
        // Iterate through teams to extract mentor-hackathon pairs
        teamList.forEach((team: any) => {
          // Validate required fields: mentorId and hackathonId must exist
          if (!team.mentorId || !team.mentorId._id || !team.hackathonId) {
            console.warn("Skipping team with missing mentor or hackathon:", team);
            return;
          }
          
          const mentorId = String(team.mentorId._id);
          let hackathonId: string | null = null;
          let hackathonName = "";
          
          // Extract hackathon ID and name from populated object
          if (typeof team.hackathonId === 'object' && team.hackathonId._id) {
            hackathonId = String(team.hackathonId._id);
            hackathonName = team.hackathonId.title || `Hackathon ${hackathonId}`;
          } else if (typeof team.hackathonId === 'string') {
            hackathonId = team.hackathonId;
            hackathonName = `Hackathon ${hackathonId}`;
          }
          
          // Skip if hackathonId couldn't be extracted
          if (!hackathonId) {
            console.warn("Skipping team with invalid hackathonId:", team);
            return;
          }
          
          // Create unique key: mentorId + hackathonId
          // This ensures each mentor appears only once per hackathon
          const pairKey = `${mentorId}--${hackathonId}`;
          
          // Only add this pair if it hasn't been seen yet
          if (!seenPairs.has(pairKey)) {
            seenPairs.add(pairKey);
            mentorAssignments.push({
              mentorId,
              mentorName: team.mentorId.name || "Unknown Mentor",
              hackathonId,
              hackathonName,
              isOnline: team.mentorId.isOnline ?? false,
              username: team.mentorId.username || team.mentorId.name,
            });
          }
        });
        
        // Map assignments to MentorInfo format for UI rendering
        const mentorList: MentorInfo[] = mentorAssignments.map((assignment) => ({
          _id: `${assignment.mentorId}-${assignment.hackathonId}`, // Composite ID for uniqueness
          mentorId: assignment.mentorId, // Actual mentor ID for API calls
          name: assignment.mentorName,
          email: "", // Removed from display
          username: assignment.username,
          isOnline: assignment.isOnline,
          hackathonName: assignment.hackathonName,
          hackathonId: assignment.hackathonId,
        }));
        
        setMentors(mentorList);
        if (mentorList.length > 0) setSelectedMentor(mentorList[0]);
      })
      .catch((e: any) => {
        console.error("❌ Failed to fetch teams:", e);
        setMentors([]);
      })
      .finally(() => setLoadingMentors(false));
  }, [token]);

  // 2. Initialize socket
  useEffect(() => {
    if (!user?.id) return;
    socketRef.current = io(process.env.REACT_APP_API_URL || "http://localhost:5000", {
      auth: { token }
    });
    return () => { socketRef.current?.disconnect(); };
  }, [user?.id, token]);

  // 3. Load messages when mentor changes
  useEffect(() => {
    if (!selectedMentor || !token) return;
    setMessages([]);
    fetchMessages(token, selectedMentor.mentorId)
      .then(data => {
        const msgList = Array.isArray(data) ? data : (data?.messages ? data.messages : []);
        setMessages(msgList);
      })
      .catch(e => {
        console.error("Failed to fetch messages:", e);
        setMessages([]);
      });
  }, [selectedMentor, token]);

  // 4. Send message
  const handleSend = async (file?: File) => {
    if (!selectedMentor || !token) return;
    const content = message.trim();
    if (!content && !file) return;
    try {
      const sent = await sendMessageApi(token, selectedMentor.mentorId, content || (file?.name ?? ""), file);
      setMessages((prev) => [...prev, sent]);
      setMessage("");
    } catch (e: any) { alert(e.message); }
  };

  // 5. Edit message
  const handleEditSave = async (msgId: string, newText: string, original: string, index: number, setErr: (i: number | null) => void) => {
    if (!token || newText === original) return;
    try {
      const updated = await editMessageApi(token, msgId, newText);
      setMessages((prev) => prev.map((m) => m._id === updated._id ? updated : m));
    } catch (e: any) {
      setErr(index);
      setTimeout(() => setErr(null), 4000);
    }
  };

  return (
    <div className="w-full h-full" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div
        className="flex flex-col sm:flex-row gap-0 bg-white w-full flex-1 min-h-0 overflow-hidden"
        style={{ minHeight: 0, minWidth: 0, display: 'flex', flex: 1, overflow: 'hidden' }}
      >
      <div className="flex flex-1 min-h-0 flex-col sm:flex-row w-full relative overflow-hidden" style={{ minHeight: 0, minWidth: 0, display: 'flex', overflow: 'hidden' }}>
        {/* Left Sidebar — Mentors List (Drawer on mobile) */}
        <div className={`absolute sm:relative w-full sm:w-72 lg:w-80 h-full sm:h-auto flex flex-col shrink-0 border-r border-[#e5e7eb] bg-white z-30 transition-transform duration-300 overflow-hidden ${selectedMentor ? "-translate-x-full sm:translate-x-0" : "translate-x-0"}`}
          style={{ minHeight: 0, minWidth: 0 }}>
          <div className="px-3 sm:px-4 py-3 sm:py-4 border-b border-[#e5e7eb] flex-shrink-0">
            <h2 className="text-sm sm:text-[15px] font-semibold text-[#1a2332]">
              My Mentors {!loadingMentors && `(${mentors.length})`}
            </h2>
          </div>

          <div className="space-y-2 flex-1 overflow-y-auto px-2 sm:px-3 py-2 sm:py-3" style={{ minHeight: 0, minWidth: 0 }}>
            {loadingMentors ? (
              <p className="text-xs sm:text-sm text-[#6e7891]">Loading mentors...</p>
            ) : mentors.length === 0 ? (
              <p className="text-xs sm:text-sm text-[#6e7891]">No mentors assigned yet.</p>
            ) : (
              mentors.map((mentor) => (
                <div
                  key={mentor._id}
                  onClick={() => setSelectedMentor(mentor)}
                  className={`p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedMentor?._id === mentor._id
                      ? "border-[#0058ab] bg-blue-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                  style={{ minWidth: 0 }}
                >
                  <div className="flex items-center justify-between gap-2 mb-1" style={{ minWidth: 0 }}>
                    <h3 className="font-semibold text-[#1a2332] text-xs sm:text-sm break-words flex-1" style={{ minWidth: 0 }}>{mentor.name}</h3>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex-shrink-0 whitespace-nowrap">
                      {mentor.isOnline ? "Online" : "Offline"}
                    </span>
                  </div>
                  {mentor.hackathonName && <p className="text-xs sm:text-sm text-[#6e7891]">{mentor.hackathonName}</p>}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Panel — Chat or Select Prompt */}
        <div className="flex-1 bg-white flex flex-col min-h-0 overflow-hidden w-full" style={{ minHeight: 0, minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!selectedMentor ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-4 sm:p-8">
              <MessageCircle className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mb-3 sm:mb-4 flex-shrink-0" />
              <h3 className="text-lg sm:text-2xl font-bold text-[#1a2332] mb-1 sm:mb-2">Select a Mentor</h3>
              <p className="text-xs sm:text-base text-[#6e7891]">Choose a mentor from the list to start chatting</p>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div className="bg-[#0058ab] px-2 sm:px-6 py-2.5 sm:py-4 flex items-center justify-between shrink-0 gap-2 sm:gap-3 min-h-[56px] sm:min-h-[70px] sticky top-0 z-10">
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold text-xs sm:text-lg break-words">{selectedMentor.name}</p>
                  <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 text-[10px] sm:text-xs min-w-0">
                    <div className={`rounded-full size-1.5 sm:size-2 flex-shrink-0 ${selectedMentor.isOnline ? "bg-[#00d5d0]" : "bg-gray-400"}`} />
                    <p className="text-white opacity-90 break-words line-clamp-1">
                      {selectedMentor.isOnline ? "Online" : "Offline"} · {selectedMentor.email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setSelectedMentor(null); setMessages([]); }}
                  className="text-white text-xs sm:text-sm opacity-80 hover:opacity-100 border border-white/30 px-2 sm:px-4 py-1 sm:py-1.5 rounded-lg flex-shrink-0 whitespace-nowrap"
                >
                  ← Back
                </button>
              </div>

              {/* Messages */}
              <MessageList
                messages={messages}
                currentUserId={user?.id ?? ""}
                openMenuIndex={openMenuIndex}
                setOpenMenuIndex={setOpenMenuIndex}
                onEditSave={handleEditSave}
                token={token ?? ""}
                socket={socketRef.current}
              />

              {/* Input bar — Fixed at bottom */}
              <div className="bg-[#f9fafb] border-t border-[#e5e7eb] px-2 sm:px-6 py-1.5 sm:py-3 shrink-0 min-h-[52px] sm:min-h-[64px] sticky bottom-0 z-10" style={{ backgroundColor: 'white' }}>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Button5 onFileSelect={(f) => handleSend(f)} />
                  <TextArea value={message} onChange={setMessage} onSend={() => handleSend()} />
                  <button
                    onClick={() => handleSend()}
                    className="bg-[#0058ab] size-7 sm:size-9 rounded-[8px] sm:rounded-[10px] flex items-center justify-center hover:bg-[#004a91] transition-colors shrink-0"
                  >
                    <svg className="size-3.5 sm:size-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
